import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Link,
  Grid,
  IconButton,
  FormControl,
  Typography,
  InputAdornment,
  Alert,
  Collapse,
  Container,
  TextField,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailTwoToneIcon from '@mui/icons-material/EmailTwoTone';
import { Controller, useForm } from 'react-hook-form';
import '../styles/Login.css';
import { AppRoutes } from '../axios/endPoints';
import Loader from '../components/common/Loader';
import { useEffect, useState } from 'react';
import { EndPoints } from '../axios/endPoints';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../axios/axiosHandler';

const schema = (t: Function) =>
  yup.object().shape({
    email: yup
      .string()
      .email(t('FORGOT_PASSWORD_VALIDATION_EMAIL'))
      .required(t('FORGOT_PASSWORD_VALIDATION_EMAIL_REQUIRED')),
  });

export default function ForgotPassword() {
  const { t } = useTranslation();
  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema(t)),
    defaultValues: {
      email: '',
    },
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const forgotPassword = (data: any) => {
    setLoading(true);
    const returnAddress = new URL(
      AppRoutes.RESET_PASSWORD,
      window.location.protocol + '//' + window.location.host
    ).href;

    axiosInstance
      .post(EndPoints.REQUEST_PASSWORD_RESET, {
        changePasswordUrl: returnAddress,
        email: data.email,
      })
      .then((payload) => {
        setLoading(false);
        setSuccess(payload.data.success);
        setError(payload.data.message);
      });
  };

  const emailSent = () => (
    <Box sx={{ mt: 2, width: '100%' }}>
      <Typography component="p">
        {t('FORGOT_PASSWORD_RECOVERY_EMAIL')}
      </Typography>
      <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
        <Link href={AppRoutes.LOG_IN} variant="body2">
          {t('FORGOT_PASSWORD_GO_TO_EMAIL')}
        </Link>
      </Grid>
    </Box>
  );

  const sendRecoveryEmail = () => (
    <Box
      component="form"
      onSubmit={handleSubmit(forgotPassword)}
      sx={{ mt: 1, width: '100%' }}
    >
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            margin='normal'
            placeholder={t('FORGOT_PASSWORD_EMAIL_ADDRESS')}
            variant="outlined"
            type="email"
            error={!!error}
            helperText={error ? errors.email?.message : ''}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <EmailTwoToneIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <Collapse in={error != null}>
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setError(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          severity="error"
        >
          {t(`${error}`)}
        </Alert>
      </Collapse>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        className="button-color"
        sx={{ mt: 3, mb: 2 }}
      >
        {t('FORGOT_PASSWORD_REQUEST_CHANGE')}
      </Button>
    </Box>
  );

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold" mb={1}>
          {t('FORGOT_PASSWORD_RESET_PASSWORD_TITLE')}
        </Typography>
        <Loader open={loading} />
        {!success ? sendRecoveryEmail() : emailSent()}
      </Box>
    </Container>
  );
}